<script lang="ts" setup>
import { ElMessageBox } from "element-plus";
import { onUnmounted } from "vue";
import { CallEventType } from "~/types/callEventType";
import { useCustomerProfileStore } from "~/stores/customerProfileStore";
import { useAutoDialerStore } from "~/projects/tele-sales/stores/autoDialerStore";
import {
  getCleanedCustomerNumber,
  getCustomerUriInfo,
  getCustomerBasicInfo,
} from "~/projects/cc/utils/callCenterHelper";
import CallService from "~/projects/cc/services/CallService";
import { useDialogStore } from "~/projects/cc/stores/dialogStore";
const customerProfileStore = useCustomerProfileStore();
const dialogStore = useDialogStore();
const incomingCallNumber = ref("");
const incomingCallQueue = ref<string | null>();
const callService = inject("callService") as CallService;
const progressCallBack = async (event: CallEventType) => {
  if (event.direction === "incoming") {
    dialogStore.setOngoingModalVisibility(false);
    dialogStore.showIncomingCallModal();
  } // auto-dialer 4
  else if (!useAutoDialerStore().autoDialer) {
    dialogStore.setOngoingModalVisibility(true);
  }
  const { phoneNumber, queueName } = getCustomerUriInfo(
    callService.getCallerUri(),
  );
  incomingCallNumber.value = getCleanedCustomerNumber(phoneNumber);
  incomingCallQueue.value = queueName;
  console.log("incoming num", incomingCallNumber.value);
  dialogStore.setOngoingCallNumber(incomingCallNumber.value);
  const [profile] = await Promise.all([
    customerProfileStore.getProfileByPhone(incomingCallNumber.value),
    dialogStore.fetchLastCallAgent(incomingCallNumber.value),
  ]);

  if (profile) dialogStore.setCurrentProfile(profile);
};

const acceptCall = () => {
  callService.acceptCall();
  dialogStore.setOngoingModalVisibility(true);
  dialogStore.setIncomingModalVisibility(false);
};

const endCall = () => {
  callService.endCall();
  dialogStore.setIncomingModalVisibility(false);
};

const handleClose = (done: () => void) => {
  ElMessageBox.confirm("Are you sure to close this dialog?")
    .then(() => {
      dialogStore.hideIncomingCallModal();
      done();
    })
    .catch(() => {
      // catch error
    });
};

const { $listen, $unListen } = useNuxtApp();
onMounted(() => {
  $listen("progress", progressCallBack);
  $listen("accept", () => {
    if (dialogStore._showIncomingCallDialog) {
      acceptCall();
    }
  });
});

onUnmounted(() => {
  $unListen("progress", progressCallBack);
  $unListen("accept");
});
</script>

<template>
  <el-dialog
    v-model="dialogStore._showIncomingCallDialog"
    :close-on-click-modal="false"
    :lock-scroll="false"
    :modal="false"
    :show-close="false"
    class="rounded-lg centerModal"
    draggable
    align-center
    width="30%"
  >
    <div class="border rounded dark:border-dark-bluegray-border">
      <div
        class="bg-gradient-to-b from-[#3D348B] via-[#130F40] to-[#000000] rounded rounded-b-none p-7 pt-10 place-items-center grid gap-32"
      >
        <div class="grid gap-2 place-items-center">
          <span class="text-white">Incoming Call</span>
          <div class="grid gap-0 text-center">
            <span class="text-white text-xs">
              Call from {{ incomingCallNumber }}
            </span>
            <span v-if="incomingCallQueue" class="text-white text-xs">
              Queue: {{ incomingCallQueue }}
            </span>
          </div>
        </div>
        <div class="grid gap-2 place-items-center">
          <span class="text-white text-xl">
            {{
              dialogStore.currentProfile
                ? `${dialogStore.currentProfile.name}`
                : "Unknown"
            }}
          </span>
          <span class="text-white text-xs">
            {{ incomingCallNumber }}
          </span>
          <span
            v-if="dialogStore.lastCallAgent"
            class="text-white/60 text-xs flex items-center gap-1"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-3 h-3 shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
            Last handled by {{ dialogStore.lastCallAgent.name }}
          </span>
        </div>
        <div class="flex justify-between w-full">
          <span class="relative flex" @click="acceptCall">
            <span
              class="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"
            ></span>
            <button
              class="bg-green-500 rounded-full p-4 w-fit place-self-center text-white mt-1 active:ring-2 active:ring-offset-2 active:ring-green-500"
            >
              <Svgs class="w-6" fill="currentColor" name="solid-phone" />
            </button>
          </span>
          <div class="relative flex" @click="endCall">
            <span
              class="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"
            ></span>
            <el-popconfirm
              title="Are you sure to close this call?"
              @confirm="endCall"
            >
              <template #reference>
                <button
                  class="bg-red-500 rounded-full p-4 w-fit place-self-center text-white active:ring-2 active:ring-offset-2 active:ring-red-500"
                >
                  <Svgs
                    class="w-6 rotate-[134deg]"
                    fill="currentColor"
                    name="solid-phone"
                  />
                </button>
              </template>
            </el-popconfirm>
          </div>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<style scoped></style>
